// src/pages/checkout/OrderConfirmation.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaCheck, FaCalendarAlt, FaCreditCard, FaTruck, FaBoxOpen, FaWhatsapp } from 'react-icons/fa';
import './OrderConfirmation.css';

const PAYMENT_LABELS = {
  cod: 'Cash on Delivery',
  bank_transfer: 'Direct Bank Transfer',
};

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderNumber = searchParams.get('order');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!orderNumber) {
      navigate('/shop');
      return;
    }
    fetchOrder();
    // eslint-disable-next-line
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const res = await fetch(`https://demo-site.storii.com.pk/demo-api/orders.php?order_number=${encodeURIComponent(orderNumber)}`);
      const data = await res.json();
      if (data.success && data.order) {
        setOrder(data.order);
      } else {
        setNotFound(true);
      }
    } catch (e) {
      console.error('Error fetching order:', e);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div className="order-confirmation">
        <div className="confirmation-container">
          <h1>Order not found</h1>
          <p className="confirmation-subtitle">We couldn't find an order with that number.</p>
          <button onClick={() => navigate('/shop')} className="continue-shopping-btn">
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const orderDate = new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const paymentTitle = PAYMENT_LABELS[order.payment_method] || order.payment_method || 'Cash on Delivery';

  // TODO: no delivery-method selection exists anywhere in CheckoutPage —
  // hardcoded to match the reference design until a real shipping-method
  // field is added to checkout and stored on the order.
  const deliveryMethod = 'Standard Delivery';
  const estimatedDelivery = '3–5 working days';

  const whatsappLink = `https://wa.me/923001234567?text=${encodeURIComponent(`Hi, I need help with my order #${order.order_number}`)}`;

  return (
    <div className="order-confirmation">
      <div className="confirmation-container">
        <div className="success-icon">
          <FaCheck />
        </div>
        <h1>Order Confirmed</h1>
        <p className="confirmation-subtitle">Thank you for shopping with UrbanThread. Your order has been received successfully.</p>

        <div className="order-number-pill">
          Order Number <strong>#{order.order_number}</strong>
        </div>

        <p className="confirmation-note">We'll send you confirmation details via SMS, email, or WhatsApp.</p>

        <div className="confirmation-info-card">
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon"><FaCalendarAlt /></div>
              <div>
                <span className="info-label">Order Date</span>
                <strong>{orderDate}</strong>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon"><FaCreditCard /></div>
              <div>
                <span className="info-label">Payment Method</span>
                <strong>{paymentTitle}</strong>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon"><FaTruck /></div>
              <div>
                <span className="info-label">Delivery Method</span>
                <strong>{deliveryMethod}</strong>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon"><FaBoxOpen /></div>
              <div>
                <span className="info-label">Estimated Delivery</span>
                <strong>{estimatedDelivery}</strong>
              </div>
            </div>
          </div>

          {order.items?.length > 0 && (
            <div className="confirmation-items">
              <h3>Items</h3>
              {order.items.map((item, idx) => (
                <div key={item.id ?? idx} className="confirmation-item-row">
                  <img src={item.image || 'https://via.placeholder.com/60'} alt={item.name} />
                  <div className="confirmation-item-info">
                    <span className="confirmation-item-name">{item.name}</span>
                    <span className="confirmation-item-qty">
                      Qty {item.quantity}
                      {item.size ? ` | Size: ${item.size}` : ''}
                    </span>
                  </div>
                  <span className="confirmation-item-price">PKR {(item.price * item.quantity).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}

          <div className="confirmation-total-row">
            <span>Total Payable</span>
            <strong>PKR {Number(order.total_amount).toLocaleString()}</strong>
          </div>
        </div>

        <div className="confirmation-actions">
          <button onClick={() => navigate('/shop')} className="continue-shopping-btn">
            Continue Shopping
          </button>
          {/* TODO: no order-tracking page exists yet — sends to Shop for now */}
          {/* 
          <button onClick={() => navigate('/shop')} className="track-order-btn">
            Track Order
          </button>
          */}
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="whatsapp-support-btn">
            <FaWhatsapp /> WhatsApp Support
          </a>
        </div>

        <p className="confirmation-footer-note">
          For order changes or urgent support, contact us on WhatsApp with your order number.
        </p>
      </div>
    </div>
  );
};

export default OrderConfirmation;